import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";

// context
import context from "../../store/context";

// assets
import Logo from "../../assets/logowhite.svg";
import LoginPattern from "../../assets/login-pattern.svg";

// icons
import CheckIcon from "../icons/Check";
import CloseIcon from "../icons/Close";

// ui
import AlertModal from "./AlertModal";

import {
  isInTelebirrSuperApp,
  isPayCancelled,
  isPaySuccess,
  startPay as telebirrStartPay,
} from "../../utils/telebirrBridge";
import {
  clearPendingPayment,
  pollInvoiceStatus,
  readPendingPayment,
  rememberPendingPayment,
} from "../../utils/paymentStatus";
import { debugLog, debugError, describeError } from "../../utils/debug";
import "../../shimmer.css";

const FEATURES = [
  "Unlimited Tenders",
  "Email Alerts",
  "SMS Alerts",
  "Telegram Alerts",
  "Save Tenders",
  "4 Languages",
  "Nationwide Coverage",
  "24/7 Support",
];

const MINIAPP_ENDPOINT = (process.env.REACT_APP_MINIAPP_ENDPOINT || "").trim();

const SCOPE = "paywall";

// A pending order older than this is left over from an abandoned attempt;
// don't reopen the paywall straight into a poll for it.
const RESUME_WINDOW_MS = 15 * 60 * 1000;

const Paywall = () => {
  const { setShowPaywall, getUserData, plans, plansError, loadPlans } =
    useContext(context);
  const navigate = useNavigate();

  const [selected, setSelected] = useState(null);
  const [paying, setPaying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [payError, setPayError] = useState(null);

  const closeTimeoutRef = useRef(null);
  // Cleared on unmount so an in-flight poll stops asking.
  const mountedRef = useRef(true);
  const pollingRef = useRef(false);

  const close = () => setShowPaywall(false);

  // stop the spinner and surface a friendly failure message
  const failPayment = (
    message = "Your payment didn't go through. Please try again.",
    { title = "Payment Unsuccessful", buttonLabel = "Try Again" } = {}
  ) => {
    setPaying(false);
    setConfirming(false);
    setPayError({ title, message, buttonLabel });
  };

  const onPaySuccess = () => {
    getUserData();
    confetti({
      particleCount: 150,
      spread: 90,
      origin: { y: 0.6 },
      zIndex: 9999,
    });
    closeTimeoutRef.current = setTimeout(() => {
      setPaying(false);
      setConfirming(false);
      setShowPaywall(false);
      navigate("/tenders");
    }, 1200);
  };

  /**
   * The bridge result only tells us the sheet closed. The invoice endpoint is
   * what actually says whether the money moved, so every non-cancelled
   * outcome ends up here. `data.status` is the only field we branch on.
   */
  const confirmPayment = async (pending) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    setConfirming(true);

    const result = await pollInvoiceStatus({
      invoiceId: pending.invoiceId,
      merchOrderId: pending.merchOrderId,
      token: sessionStorage.getItem("token"),
      shouldContinue: () => mountedRef.current,
    });

    pollingRef.current = false;
    if (!mountedRef.current) return;

    debugLog(SCOPE, "invoice confirmation finished", {
      outcome: result.outcome,
      invoiceId: pending.invoiceId,
    });
    setConfirming(false);

    switch (result.outcome) {
      case "paid":
        // Authoritative: the server has already activated the subscription.
        clearPendingPayment();
        onPaySuccess();
        break;
      case "failed":
        // A retry has to create a brand new order — telebirr rejects a reused
        // merch_order_id — which is exactly what the Try Again button does.
        clearPendingPayment();
        failPayment();
        break;
      case "support":
        clearPendingPayment();
        failPayment(
          "We couldn't verify this payment. Please contact support and we'll sort it out.",
          { title: "Payment Needs Review", buttonLabel: "Got it" }
        );
        break;
      case "timeout":
        // Leave the pending order in place: the callback is still the primary
        // path and may land after we stop asking, and reopening rechecks.
        failPayment(
          "Your payment is still processing. We'll confirm it shortly — no need to pay again.",
          { title: "Still Processing", buttonLabel: "Got it" }
        );
        break;
      default:
        break;
    }
  };

  // H5 Checkout docs: handleinitDataCallback() has no args — that invocation
  // means the sheet finished, not that it was paid. MiniApp: resultCode "1".
  // Either way we confirm against the server; only an explicit cancel is
  // trusted on its own, because nothing was charged.
  const startPay = (rawRequest, pending) => {
    const cancelPayment = () => {
      clearPendingPayment();
      failPayment("You cancelled the payment. No charge was made.");
    };

    telebirrStartPay(rawRequest)
      .then((res) => {
        debugLog(SCOPE, "startPay result", res);
        if (isPayCancelled(res)) {
          cancelPayment();
          return;
        }
        if (!pending) {
          // No invoice to poll — fall back to the bridge's own signal.
          if (isPaySuccess(res)) onPaySuccess();
          else failPayment();
          return;
        }
        confirmPayment(pending);
      })
      .catch((err) => {
        debugError(SCOPE, "startPay failed", describeError(err));
        // Macle reports a closed sheet through fail(), so it lands here.
        if (isPayCancelled(err) || !pending) cancelPayment();
        else confirmPayment(pending);
      });
  };

  // 1. Pay Now → get a raw_request order string from our backend, then hand
  // it to the superapp's native payment bridge
  const subscribe = () => {
    if (!selected) return;
    setPaying(true);
    setPayError(null);

    if (!isInTelebirrSuperApp()) {
      failPayment(
        "This payment needs to be completed inside the telebirr SuperApp."
      );
      return;
    }

    const userToken = sessionStorage.getItem("token");

    fetch(
      `${MINIAPP_ENDPOINT}/api/miniapp/telebirr-create-order?plan_id=${selected}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${userToken}`,
        },
      }
    )
      .then((res) => res.json().catch(() => null))
      .then((json) => {
        debugLog(SCOPE, "create-order", json);
        if (!json || !json.raw_request) {
          failPayment(json && json.message);
          return;
        }

        const invoiceId =
          json.invoice_id || json.invoiceId || (json.invoice && json.invoice.id);
        const merchOrderId =
          json.merch_order_id || json.merchOrderId || json.out_trade_no;

        let pending = null;
        if (invoiceId) {
          // Overwrite any previous order: the old merch_order_id is dead, and
          // reusing it is what telebirr rejects with 60320014.
          pending = { invoiceId, merchOrderId, createdAt: Date.now() };
          rememberPendingPayment(pending);
        } else {
          debugError(SCOPE, "create-order returned no invoice id", json);
        }

        startPay(json.raw_request, pending);
      })
      .catch((error) => {
        debugError(SCOPE, "create-order failed", describeError(error));
        failPayment();
      });
  };

  // stop pending timers and polls if the paywall unmounts mid-flight
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  // Plans are normally prefetched at login; load them here if they weren't
  // (active account, or the prefetch hasn't landed).
  useEffect(() => {
    if (plans === null) loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Default to the first plan once they arrive, and drop a selection that
  // disappears on reload.
  useEffect(() => {
    if (!plans) return;
    setSelected((cur) =>
      plans.some((p) => p.id === cur) ? cur : plans[0] ? plans[0].id : null
    );
  }, [plans]);

  // The SuperApp reloads the H5 page after checkout (its own sample does this),
  // which throws away everything above. Pick the order back up from storage.
  useEffect(() => {
    const pending = readPendingPayment();
    if (!pending) return;

    if (Date.now() - (pending.createdAt || 0) > RESUME_WINDOW_MS) {
      clearPendingPayment();
      return;
    }

    debugLog(SCOPE, "resuming confirmation for a pending order", pending);
    setPaying(true);
    confirmPayment(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPlan = (plans || []).find((p) => p.id === selected);

  return (
    <div className="fixed inset-0 z-[150] bg-[#352a66] flex flex-col animate-sheet-fade">
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* dark indigo hero */}
        <div className="relative shrink-0 text-white px-6 pt-10 pb-7 text-center bg-gradient-to-b from-[#1f1748] to-[#352a66]">
          {/* halftone pattern texture */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url(${LoginPattern})`,
              backgroundSize: "cover",
              backgroundPosition: "bottom",
              backgroundRepeat: "no-repeat",
              opacity: 0.22,
            }}
          />
          <button
            onClick={close}
            aria-label="Close"
            className="absolute top-5 right-5 z-10 w-9 h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center active:scale-95 transition-transform"
          >
            <CloseIcon styles="w-5 h-5" />
          </button>
          <div className="relative">
            <img src={Logo} alt="iChereta" className="h-8 mx-auto mb-3" />
            <h1 className="text-[24px] font-bold leading-tight">
              Unlock Full Access
            </h1>
            <p className="mt-1 text-white/80 text-[13px]">
              All tenders. All channels. All over Ethiopia.
            </p>
          </div>
        </div>

        {/* content cutout — rounded top curving into the indigo hero */}
        <div className="flex-1 rounded-t-[28px] bg-gray-50 px-5 pt-6 pb-6">
          {/* features */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100">
            <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-start gap-2">
                  <span className="w-4 h-4 mt-[1px] rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                    <CheckIcon styles="w-2.5 h-2.5 text-green-600" />
                  </span>
                  <span className="text-[13px] text-gray-700 leading-snug">
                    {f}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* plans */}
          <div className="mt-6">
            <h2 className="px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Choose your plan
            </h2>
            <div className="flex flex-col gap-3">
            {plans === null &&
              [0, 1].map((i) => (
                <div
                  key={i}
                  aria-hidden="true"
                  className="w-full flex items-center gap-3 p-4 rounded-2xl border border-gray-200 bg-white"
                >
                  <span className="w-6 h-6 rounded-full shrink-0 animate-pulse-gradient" />
                  <div className="flex-1 min-w-0 flex flex-col gap-2">
                    <span className="h-3.5 w-[45%] rounded-full animate-pulse-gradient" />
                    <span className="h-2.5 w-[35%] rounded-full animate-pulse-gradient" />
                  </div>
                  <span className="h-4 w-16 rounded-full shrink-0 animate-pulse-gradient" />
                </div>
              ))}
            {plans !== null && plans.length === 0 && (
              <div className="p-4 rounded-2xl border border-gray-200 bg-white text-center">
                <p className="text-sm text-gray-600">
                  {plansError
                    ? "We couldn't load the plans."
                    : "No plans are available right now."}
                </p>
                <button
                  onClick={loadPlans}
                  className="mt-2 text-sm font-semibold text-[#664bc7]"
                >
                  Try again
                </button>
              </div>
            )}
            {(plans || []).map((plan) => {
              const active = selected === plan.id;
              return (
                <button
                  key={plan.id}
                  onClick={() => setSelected(plan.id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors ${
                    active
                      ? "border-[#664bc7] ring-1 ring-[#664bc7] bg-[#664bc7]/[0.05]"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <span
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      active
                        ? "border-[#664bc7] bg-[#664bc7]"
                        : "border-gray-300"
                    }`}
                  >
                    {active && <CheckIcon styles="w-4 h-4 text-white" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {plan.time}
                      </span>
                      {plan.badge && (
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-green-500 text-white px-2 py-0.5 rounded-full">
                          {plan.badge}
                        </span>
                      )}
                    </div>
                    {plan.perMonth && (
                      <span className="text-xs text-gray-500">
                        ≈ {plan.perMonth} birr / month
                      </span>
                    )}
                  </div>
                  <span className="font-bold text-gray-900 shrink-0">
                    {plan.price}{" "}
                    <span className="text-xs font-normal text-gray-500">
                      birr
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
            <p className="px-1 pt-3 text-[11px] text-gray-400 text-center">
              All prices include 15% VAT.
            </p>
          </div>
        </div>
      </div>

      {/* sticky CTA */}
      <div className="border-t border-gray-200 bg-white/90 backdrop-blur-md px-5 pt-4 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        <button
          onClick={subscribe}
          disabled={paying || !selectedPlan}
          className="w-full h-[52px] rounded-2xl text-white font-semibold text-[17px] relative overflow-hidden bg-gradient-to-b from-[#7c61e0] to-[#5b40b8] shadow-[0_4px_12px_rgba(102,75,199,0.40),inset_0_1px_0_rgba(255,255,255,0.30),inset_0_-2px_4px_rgba(74,54,149,0.55)] active:shadow-[inset_0_2px_6px_rgba(74,54,149,0.60)] active:translate-y-px transition-all duration-150 disabled:opacity-70"
        >
          {paying ? (
            <span className="absolute inset-0 flex items-center justify-center gap-3">
              <span className="w-6 h-6 border-t-2 border-white rounded-full animate-spin" />
              {confirming && (
                <span className="text-[15px] font-medium">
                  Confirming your payment…
                </span>
              )}
            </span>
          ) : (
            <span className="block text-center">
              {selectedPlan
                ? `Pay Now · ${selectedPlan.price} birr`
                : "Pay Now"}
            </span>
          )}
        </button>
      </div>

      {payError && (
        <AlertModal
          title={payError.title}
          message={payError.message}
          buttonLabel={payError.buttonLabel || "Try Again"}
          onClose={() => setPayError(null)}
        />
      )}
    </div>
  );
};

export default Paywall;
